import { CurrencyPipe, NgOptimizedImage } from '@angular/common'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    computed,
    input,
    output,
    signal,
} from '@angular/core'

import { environment } from '../../../../environments/environment'
import type { BookingUpsellMenuItem, BookingUpsellModifierGroup } from '../../../domain/Availability'
import { Availability } from '../../../domain/Availability'
import { usesGenericPaymentTerminology } from '../../../domain/BookingPaymentTerminology'
import { richTextPlainText, truncateRichTextHtml } from '../../../utilities/rich-text'
import type { VenueStorefrontBookingLineItem } from '../venue-storefront-booking-flow/venue-storefront-booking-flow.types'

@Component({
    selector: 'app-venue-storefront-booking-upsell-step',
    standalone: true,
    imports: [NgOptimizedImage, CurrencyUnitPipe],
    providers: [CurrencyPipe],
    templateUrl: './venue-storefront-booking-upsell-step.component.html',
    styleUrls: ['./venue-storefront-booking-upsell-step.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingUpsellStepComponent implements AfterViewInit {
    availability = input.required<Availability>()
    reasonId = input.required<string | null>()
    partySize = input.required<number>()
    showPrices = input(false)
    lineItems = input<VenueStorefrontBookingLineItem[]>([])
    lineItemsChange = output<VenueStorefrontBookingLineItem[]>()
    confirmed = output<VenueStorefrontBookingLineItem[]>()

    @ViewChild('heading', { static: true }) private heading!: ElementRef<HTMLHeadingElement>

    protected readonly menu = computed(() => {
        return this.availability().bookingUpsellMenu(this.reasonId())
    })
    protected readonly menuDescription = computed((): string => {
        return this.menu()?.description || (usesGenericPaymentTerminology(this.availability().venue.id, this.showPrices())
            ? 'Your selections will be included with your booking.'
            : "We'll let the kitchen know ahead of your visit.")
    })
    protected readonly items = computed(() => {
        return this.availability().bookingUpsellMenuItems(this.reasonId())
    })
    protected readonly requiredQuantity = computed((): number | null => {
        return this.menu()?.requiredBookingQuantity(this.partySize()) ?? null
    })
    protected readonly totalQuantity = computed((): number => {
        return this.lineItems().reduce((total, item): number => {
            return total + item.quantity
        }, 0)
    })
    protected readonly canIncrement = computed((): boolean => {
        const required = this.requiredQuantity()
        return required === null || this.totalQuantity() < required
    })
    protected readonly validQuantity = computed((): boolean => {
        const required = this.requiredQuantity()
        return required === null || this.totalQuantity() === required
    })
    protected readonly productImageBaseUrl = `${environment.cdnUrl}/192x192/product-images/`
    protected readonly expandedDescriptionIds = signal<Set<string>>(new Set())
    protected readonly submitted = signal(false)
    protected readonly descriptionCharacterLimit = 100

    ngAfterViewInit() {
        this.heading.nativeElement.focus({ preventScroll: true })
    }

    protected quantity(item: BookingUpsellMenuItem): number {
        return this.lineItems().filter(lineItem => (lineItem.menuItemId ? lineItem.menuItemId === item.menuItemId : lineItem.skuId === item.skuId))
            .reduce((quantity, lineItem) => quantity + lineItem.quantity, 0)
    }

    protected selections(item: BookingUpsellMenuItem): VenueStorefrontBookingLineItem[] {
        return this.lineItems().filter(lineItem => (lineItem.menuItemId ? lineItem.menuItemId === item.menuItemId : lineItem.skuId === item.skuId))
            .flatMap(lineItem => Array.from({ length: lineItem.quantity }, () => ({ ...lineItem, quantity: 1 })))
    }

    protected increment(item: BookingUpsellMenuItem) {
        if (!this.canIncrement()) {
            return
        }
        if (item.modifierGroups.length > 0) {
            this.lineItemsChange.emit([...this.lineItems(), {
                menuItemId: item.menuItemId,
                productId: item.productId,
                skuId: item.skuId,
                quantity: 1,
                modifierSkuIds: [],
            }])
            return
        }
        this.updateQuantity(item, this.quantity(item) + 1)
    }

    protected decrement(item: BookingUpsellMenuItem) {
        if (item.modifierGroups.length > 0) {
            this.removeSelection(item, this.quantity(item) - 1)
            return
        }
        this.updateQuantity(item, Math.max(this.quantity(item) - 1, 0))
    }

    protected confirm() {
        if (!this.validQuantity()) {
            return
        }
        this.submitted.set(true)
        if (this.items().some(item => this.selections(item).some(selection =>
            item.modifierGroups.some(group => this.modifierGroupError(selection, group))
        ))) {
            return
        }
        this.confirmed.emit(this.lineItems())
    }

    protected removeSelection(item: BookingUpsellMenuItem, index: number) {
        this.updateSelections(item, this.selections(item).filter((_, selectionIndex) => selectionIndex !== index))
    }

    protected toggleModifier(item: BookingUpsellMenuItem, index: number, group: BookingUpsellModifierGroup, skuId: string) {
        const selections = this.selections(item)
        const selection = selections[index]
        if (!selection || this.modifierDisabled(selection, group, skuId)) {
            return
        }
        const modifierSkuIds = selection.modifierSkuIds ?? []
        selections[index] = {
            ...selection,
            modifierSkuIds: modifierSkuIds.includes(skuId)
                ? modifierSkuIds.filter(id => id !== skuId)
                : [...modifierSkuIds, skuId],
        }
        this.updateSelections(item, selections)
    }

    protected modifierDisabled(selection: VenueStorefrontBookingLineItem, group: BookingUpsellModifierGroup, skuId: string): boolean {
        return !selection.modifierSkuIds?.includes(skuId)
            && group.maximumModifiersRequired !== null
            && this.selectedModifierCount(selection, group) >= group.maximumModifiersRequired
    }

    protected modifierGroupLimits(group: BookingUpsellModifierGroup): string | null {
        const minimum = group.minimumModifiersRequired
        const maximum = group.maximumModifiersRequired
        if (minimum !== null && maximum !== null) {
            return minimum === maximum
                ? `Select exactly ${minimum} ${minimum === 1 ? 'option' : 'options'}`
                : `Select ${minimum}-${maximum} options`
        }
        if (minimum !== null) {
            return `Select at least ${minimum} ${minimum === 1 ? 'option' : 'options'}`
        }
        if (maximum !== null) {
            return `Select up to ${maximum} ${maximum === 1 ? 'option' : 'options'}`
        }
        return null
    }

    protected modifierGroupError(selection: VenueStorefrontBookingLineItem, group: BookingUpsellModifierGroup): string | null {
        const count = this.selectedModifierCount(selection, group)
        const minimum = group.minimumModifiersRequired
        const maximum = group.maximumModifiersRequired
        if (minimum !== null && count < minimum) {
            return `Please select at least ${minimum} ${minimum === 1 ? 'option' : 'options'}`
        }
        if (maximum !== null && count > maximum) {
            return `Please select no more than ${maximum} ${maximum === 1 ? 'option' : 'options'}`
        }
        return null
    }

    private selectedModifierCount(selection: VenueStorefrontBookingLineItem, group: BookingUpsellModifierGroup): number {
        return group.modifiers.filter(modifier => selection.modifierSkuIds?.includes(modifier.skuId)).length
    }

    private updateSelections(item: BookingUpsellMenuItem, selections: VenueStorefrontBookingLineItem[]) {
        this.lineItemsChange.emit([
            ...this.lineItems().filter(lineItem => (lineItem.menuItemId ? lineItem.menuItemId !== item.menuItemId : lineItem.skuId !== item.skuId)),
            ...selections,
        ])
    }

    protected descriptionCanExpand(item: BookingUpsellMenuItem): boolean {
        return richTextPlainText(item.description).length > this.descriptionCharacterLimit
    }

    protected descriptionContent(item: BookingUpsellMenuItem): string {
        const description = item.description ?? ''
        return this.expandedDescriptionIds().has(item.menuItemId)
            ? description
            : truncateRichTextHtml(description, this.descriptionCharacterLimit)
    }

    protected descriptionIsExpanded(item: BookingUpsellMenuItem): boolean {
        return this.expandedDescriptionIds().has(item.menuItemId)
    }

    protected toggleDescription(item: BookingUpsellMenuItem) {
        this.expandedDescriptionIds.update(expandedIds => {
            const updatedIds = new Set(expandedIds)
            if (updatedIds.has(item.menuItemId)) {
                updatedIds.delete(item.menuItemId)
            } else {
                updatedIds.add(item.menuItemId)
            }
            return updatedIds
        })
    }

    private updateQuantity(item: BookingUpsellMenuItem, quantity: number) {
        const otherLineItems = this.lineItems().filter(lineItem => (lineItem.menuItemId ? lineItem.menuItemId !== item.menuItemId : lineItem.skuId !== item.skuId))
        this.lineItemsChange.emit(quantity === 0
            ? otherLineItems
            : [...otherLineItems, {
                menuItemId: item.menuItemId,
                productId: item.productId,
                skuId: item.skuId,
                quantity,
            }]
        )
    }
}
